import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Share2,  
  Search, 
  Download, 
  Eye,
  Calendar,
  X,
  Check,
  CheckCircle2,
  Plus,
  Minus,
  AlertCircle,
  Building2,
  Navigation,
  CreditCard,
  BookOpen
} from 'lucide-react';
import InvoiceModal from './InvoiceModal';
import CustomerLedgerModal from './CustomerLedgerModal';

export default function BillingLedger({ 
  currentTenant,
  tenant, 
  centers = [],
  lines = [],
  routes = [], 
  products = [], 
  customers = [], 
  features,
  deliveries = {},
  onUpdateCustomer,
  onNavigateToPayments,
  lang = 'mr'
}) {
  const isMr = lang === 'mr';
  const effectiveTenant = currentTenant || tenant;

  const [selectedMonth, setSelectedMonth] = useState('2026-09');
  const [isAutoScanOpen, setIsAutoScanOpen] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanResults, setScanResults] = useState([]);
  const [selectedScanItems, setSelectedScanItems] = useState([]);
  const [isResolvingScan, setIsResolvingScan] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [modalInvoice, setModalInvoice] = useState(null);
  const [selectedLedgerCustomer, setSelectedLedgerCustomer] = useState(null);
  const [liveBillingRecords, setLiveBillingRecords] = useState([]);
  const [loadingBilling, setLoadingBilling] = useState(false);

  // Center & Route (Line) Filtering States - Default 'ALL' (सगळे दिसू देत)
  const [selectedCenterId, setSelectedCenterId] = useState('ALL');
  const [selectedLineId, setSelectedLineId] = useState('ALL');

  // Internal centers and lines state (with live fallback if empty)
  const [internalCenters, setInternalCenters] = useState(centers || []);
  const [internalLines, setInternalLines] = useState(lines || routes || []);

  const tenantSlug = effectiveTenant?.slug || 'samarth-krupa-dairy';
  const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';

  // Feature detection: Check if dairy byproducts or bakery products are enabled by developer
  const effectiveFeatures = features || effectiveTenant?.features || effectiveTenant?.feature || {};
  const isByproductsEnabled = Boolean(
    effectiveFeatures?.dairy_byproducts || 
    effectiveFeatures?.bakery_products
  );

  // Check if WhatsApp feature is enabled (Default on for Advanced plan, or if feature flag is active)
  const isAdvancedPlan = (effectiveTenant?.plan_id || 1) >= 2;
  const isWhatsappFeatureOn = effectiveFeatures?.whatsapp_billing !== undefined
    ? Boolean(effectiveFeatures.whatsapp_billing)
    : isAdvancedPlan;

  // Calendar Editor Modal States
  const [calendarCustomer, setCalendarCustomer] = useState(null);
  const [calendarDays, setCalendarDays] = useState([]);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  const [savingCalendar, setSavingCalendar] = useState(false);
  const [calendarToast, setCalendarToast] = useState(null);
  const [calendarDefaultQuota, setCalendarDefaultQuota] = useState(0);
  const [calendarDefaultCowQuota, setCalendarDefaultCowQuota] = useState(0);
  const [calendarDefaultBufQuota, setCalendarDefaultBufQuota] = useState(0);
  const [calendarRate, setCalendarRate] = useState(0);
  const [calendarCowRate, setCalendarCowRate] = useState(56);
  const [calendarBufRate, setCalendarBufRate] = useState(72);

  // Synchronize props to internal centers/lines or fetch fallback
  useEffect(() => {
    if (centers && centers.length > 0) setInternalCenters(centers);
    if (lines && lines.length > 0) setInternalLines(lines);
    else if (routes && routes.length > 0) setInternalLines(routes);
  }, [centers, lines, routes]);

  useEffect(() => {
    if ((!centers || centers.length === 0) && tenantSlug) {
      fetch(`${API_BASE}/tenant/${tenantSlug}/centers-lines`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            if (Array.isArray(data.centers) && data.centers.length > 0) setInternalCenters(data.centers);
            if (Array.isArray(data.lines) && data.lines.length > 0) setInternalLines(data.lines);
          }
        })
        .catch(() => {});
    }
  }, [tenantSlug, centers, API_BASE]);

  // Available lines filtered by selected center (if center is selected)
  const availableLines = useMemo(() => {
    if (selectedCenterId === 'ALL') return internalLines;
    return internalLines.filter(l => String(l.center_id) === String(selectedCenterId));
  }, [internalLines, selectedCenterId]);

  // Fetch 100% Live DB Calculated Billing Summary
  const fetchLiveBilling = useCallback(async () => {
    setLoadingBilling(true);
    try {
      const res = await fetch(`${API_BASE}/tenant/${tenantSlug}/billing/monthly-summary?year_month=${selectedMonth}`);
      const data = await res.json();
      if (data.success && Array.isArray(data.billing_records)) {
        setLiveBillingRecords(data.billing_records);
      }
    } catch (err) {
      console.warn('Could not fetch monthly summary API, fallback to client calculation:', err);
    } finally {
      setLoadingBilling(false);
    }
  }, [API_BASE, tenantSlug, selectedMonth]);

  useEffect(() => {
    fetchLiveBilling();
  }, [fetchLiveBilling]);

  // Use Live DB Records if available, else fallback to customers map
  const billingRecords = liveBillingRecords.length > 0 ? liveBillingRecords.map(r => ({
    ...r,
    customerName: isMr ? (r.customerName || 'ग्राहक #' + r.customerId) : (r.customerNameEn || r.customerName || 'Customer #' + r.customerId)
  })) : customers.map(cust => {
    const isDemand = (cust.subscription_type || cust.subscriptionType || '').toString().trim().toUpperCase().replace(/[- ]/g, '_') === 'ON_DEMAND';
    const mCow = parseFloat(cust.morning_cow_qty || 0);
    const mBuf = parseFloat(cust.morning_buf_qty || 0);
    const eCow = parseFloat(cust.evening_cow_qty || 0);
    const eBuf = parseFloat(cust.evening_buf_qty || 0);
    const dailyCow = mCow + eCow;
    const dailyBuf = mBuf + eBuf;

    const cowProd = products?.find(p => (p.category === 'Milk' || p.category === 'दूध') && (p.name_mr?.includes('गाय') || p.name?.toLowerCase().includes('cow')));
    const bufProd = products?.find(p => (p.category === 'Milk' || p.category === 'दूध') && (p.name_mr?.includes('म्हैस') || p.name?.toLowerCase().includes('buf')));
    const defaultCowRate = parseFloat(cowProd?.retail_rate || 56);
    const defaultBufRate = parseFloat(bufProd?.retail_rate || 72);

    const custCowRate = parseFloat(cust.cow_rate || cust.custom_cow_rate || defaultCowRate);
    const custBufRate = parseFloat(cust.buf_rate || cust.custom_buf_rate || defaultBufRate);

    const dailyTotal = isDemand ? 0 : (dailyCow + dailyBuf);
    const deliveredDays = isDemand ? 0 : (cust.status === 'Vacation' ? 18 : 30);
    const totalMilkLiters = parseFloat((dailyTotal * deliveredDays).toFixed(1));
    const milkAmount = Math.round(((dailyCow * custCowRate) + (dailyBuf * custBufRate)) * deliveredDays);
    const prevBalance = parseFloat(cust.balance || cust.opening_balance || 0);
    const totalBill = milkAmount + prevBalance;

    return {
      customerId: cust.id,
      customerName: isMr ? (cust.name_mr || cust.name || 'ग्राहक #' + cust.id) : (cust.name_en || cust.name || 'Customer #' + cust.id),
      phone: cust.phone || '',
      address: cust.address || cust.line_name || '',
      routeId: cust.line_id || '',
      lineName: cust.line_name || '',
      centerId: cust.center_id || '',
      deliveredDays,
      totalMilkLiters,
      milkAmount,
      extraAmount: 0,
      prevBalance,
      totalBill,
      paidAmount: 0,
      pendingAmount: totalBill,
      paymentStatus: 'PENDING'
    };
  });

  // Filter records by Search, Payment Status, Center, and Route/Line
  const filtered = billingRecords.filter(rec => {
    // 1. Text Search
    const q = (searchQuery || '').trim().toLowerCase();
    const cName = (rec.customerName || '').toLowerCase();
    const cPhone = (rec.phone || '');
    const cAddr = (rec.address || '').toLowerCase();
    const matchesSearch = !q || cName.includes(q) || cPhone.includes(q) || cAddr.includes(q);

    // 2. Payment Status Filter
    const matchesStatus = filterStatus === 'ALL' || rec.paymentStatus === filterStatus;

    // 3. Route / Line Filter
    let matchesLine = true;
    if (selectedLineId !== 'ALL') {
      matchesLine = String(rec.routeId) === String(selectedLineId);
    }

    // 4. Center Filter
    let matchesCenter = true;
    if (selectedCenterId !== 'ALL') {
      // Find line object to match center
      const lineObj = internalLines.find(l => String(l.id) === String(rec.routeId));
      if (lineObj) {
        matchesCenter = String(lineObj.center_id) === String(selectedCenterId);
      } else if (rec.centerId || rec.center_id) {
        matchesCenter = String(rec.centerId || rec.center_id) === String(selectedCenterId);
      } else {
        const cust = customers.find(c => String(c.id) === String(rec.customerId));
        const custLine = internalLines.find(l => String(l.id) === String(cust?.line_id));
        matchesCenter = custLine ? String(custLine.center_id) === String(selectedCenterId) : false;
      }
    }

    return matchesSearch && matchesStatus && matchesLine && matchesCenter;
  });

  // Calculated Financial Metrics based on Filtered List
  const totalBilled = filtered.reduce((sum, r) => sum + r.totalBill, 0);
  const totalReceived = filtered.reduce((sum, r) => sum + r.paidAmount, 0);
  const totalDue = filtered.reduce((sum, r) => sum + r.pendingAmount, 0);

  // Open Monthly Delivery & Skipped Days (खाडे) Editor
  // Auto-Scan Ghost & Mismatched Delivery/Attendance entries
  const handleOpenAutoScan = async () => {
    setIsAutoScanOpen(true);
    setScanLoading(true);
    setScanResults([]);
    setSelectedScanItems([]);

    try {
      const res = await fetch(`${API_BASE}/tenant/${tenantSlug}/billing/auto-scan-mismatches?year_month=${selectedMonth}`);
      const data = await res.json();
      if (data.success && Array.isArray(data.mismatches)) {
        setScanResults(data.mismatches);
        setSelectedScanItems(data.mismatches.map(m => m.id));
      } else {
        setScanResults([]);
      }
    } catch (err) {
      console.error('Failed to run auto-scan:', err);
    } finally {
      setScanLoading(false);
    }
  };

  const handleResolveScan = async () => {
    if (selectedScanItems.length === 0) {
      alert(isMr ? 'कृपया दुरुस्त करण्यासाठी किमान एक नोंद निवडा.' : 'Please select at least one record.');
      return;
    }

    const itemsToResolve = scanResults.filter(m => selectedScanItems.includes(m.id));
    const confirmMsg = isMr
      ? '⚠️ एकूण ' + itemsToResolve.length + ' विसंगत नोंदी हटवून/दुरुस्त करायच्या आहेत का?\nयामुळे रोजनिशी व मासिक हजेरीतील विसंगती कायमची दूर होईल.'
      : 'Are you sure you want to resolve/delete ' + itemsToResolve.length + ' mismatched records?';

    if (!window.confirm(confirmMsg)) return;

    setIsResolvingScan(true);
    try {
      const res = await fetch(`${API_BASE}/tenant/${tenantSlug}/billing/resolve-mismatch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: itemsToResolve })
      });
      const data = await res.json();
      if (data.success) {
        alert(data.message || 'नोंदी यशस्वीरित्या दुरुस्त झाल्या.');
        setIsAutoScanOpen(false);
        // Refresh billing records
        window.location.reload();
      } else {
        alert(data.message || 'त्रुटी आली.');
      }
    } catch (err) {
      alert('सर्व्हरशी संपर्क होऊ शकला नाही: ' + err.message);
    } finally {
      setIsResolvingScan(false);
    }
  };

  const handleOpenCalendar = async (rec) => {
    setCalendarCustomer(rec);
    setLoadingCalendar(true);
    setCalendarToast(null);

    // Calculate days in selected month
    const [yr, mo] = selectedMonth.split('-').map(Number);
    const daysInMonth = new Date(yr, mo, 0).getDate();

    // Match full customer info from customers prop
    const cust = customers.find(c => String(c.id) === String(rec.customerId));
    const rawSub = (cust?.subscription_type || rec?.subscription_type || cust?.subscriptionType || rec?.subscriptionType || '').toString().trim().toUpperCase().replace(/[- ]/g, '_');
    const isCustOnDemand = rawSub === 'ON_DEMAND';

    const mCow = parseFloat(cust?.morning_cow_qty || 0);
    const mBuf = parseFloat(cust?.morning_buf_qty || 0);
    const eCow = parseFloat(cust?.evening_cow_qty || 0);
    const eBuf = parseFloat(cust?.evening_buf_qty || 0);
    const masterCowSum = parseFloat((mCow + eCow).toFixed(1));
    const masterBufSum = parseFloat((mBuf + eBuf).toFixed(1));
    const masterDailySum = masterCowSum + masterBufSum;

    const defaultCowQuota = isCustOnDemand ? 0 : masterCowSum;
    const defaultBufQuota = isCustOnDemand ? 0 : masterBufSum;
    const defaultQuota = isCustOnDemand ? 0 : parseFloat(masterDailySum.toFixed(1));

    setCalendarDefaultCowQuota(defaultCowQuota);
    setCalendarDefaultBufQuota(defaultBufQuota);
    setCalendarDefaultQuota(defaultQuota);

    // Resolve rates
    const cRate = parseFloat(cust?.cow_rate || cust?.custom_cow_rate || 0);
    const bRate = parseFloat(cust?.buf_rate || cust?.custom_buf_rate || 0);
    const cowProd = products?.find(p => (p.category === 'Milk' || p.category === 'दूध') && (p.name_mr?.includes('गाय') || p.name?.toLowerCase().includes('cow')));
    const bufProd = products?.find(p => (p.category === 'Milk' || p.category === 'दूध') && (p.name_mr?.includes('म्हैस') || p.name?.toLowerCase().includes('buf')));
    const defaultCowRate = parseFloat(cowProd?.retail_rate || 56);
    const defaultBufRate = parseFloat(bufProd?.retail_rate || 72);

    const activeCowRate = cRate > 0 ? cRate : defaultCowRate;
    const activeBufRate = bRate > 0 ? bRate : defaultBufRate;

    setCalendarCowRate(parseFloat(activeCowRate.toFixed(2)));
    setCalendarBufRate(parseFloat(activeBufRate.toFixed(2)));

    let avgRate = activeCowRate;
    if (masterBufSum > 0 && masterCowSum === 0) avgRate = activeBufRate;
    else if (masterBufSum > 0 && masterCowSum > 0) avgRate = (activeCowRate + activeBufRate) / 2;
    setCalendarRate(parseFloat(avgRate.toFixed(2)));

    // 1. If live billing record has calendar or dailyCalendar pre-populated from monthly-summary, use it!
    const liveCal = (Array.isArray(rec.dailyCalendar) && rec.dailyCalendar.length > 0)
      ? rec.dailyCalendar
      : ((Array.isArray(rec.calendar) && rec.calendar.length > 0) ? rec.calendar : null);

    if (liveCal) {
      const mapped = Array.from({ length: daysInMonth }, (_, i) => {
        const dayNum = i + 1;
        const d = liveCal.find(item => item.day === dayNum);
        const dateObj = new Date(yr, mo - 1, dayNum);
        const isAbsent = d ? (d.status === 'ABSENT' || d.status === 'VACATION' || Boolean(d.isAbsent)) : (isCustOnDemand ? true : false);

        let cowL = 0;
        let bufL = 0;
        if (d && !isAbsent) {
          cowL = parseFloat(d.cow_qty !== undefined ? d.cow_qty : (d.cow_litres || d.cowLitres || 0));
          bufL = parseFloat(d.buf_qty !== undefined ? d.buf_qty : (d.buf_litres || d.bufLitres || 0));
          const totalRaw = parseFloat(d.total_qty !== undefined ? d.total_qty : (d.litres || 0));
          if (cowL === 0 && bufL === 0 && totalRaw > 0) {
            if (masterBufSum > 0 && masterCowSum === 0) {
              bufL = totalRaw;
            } else {
              cowL = totalRaw;
            }
          }
        } else if (!isAbsent) {
          cowL = defaultCowQuota;
          bufL = defaultBufQuota;
        }

        const dayTotal = Math.round((cowL + bufL) * 100) / 100;
        return {
          day: dayNum,
          dayOfWeek: dateObj.getDay(),
          cowLitres: cowL,
          bufLitres: bufL,
          litres: dayTotal,
          isAbsent: isCustOnDemand ? (dayTotal <= 0) : isAbsent
        };
      });
      setCalendarDays(mapped);
      setLoadingCalendar(false);
      return;
    }

    // 2. Fallback: fetch live attendance logs from API
    try {
      const res = await fetch(`${API_BASE}/tenant/${tenantSlug}/customers/${rec.customerId}/attendance?year_month=${selectedMonth}`);
      const data = await res.json();
      if (data.success && Array.isArray(data.attendance) && data.attendance.length > 0) {
        const dbMap = {};
        data.attendance.forEach(item => {
          dbMap[item.day_of_month] = item;
        });
        const mapped = Array.from({ length: daysInMonth }, (_, i) => {
          const dayNum = i + 1;
          const dateObj = new Date(yr, mo - 1, dayNum);
          const dbRec = dbMap[dayNum];
          if (dbRec) {
            const isAbsent = Boolean(dbRec.is_absent);
            let cowL = parseFloat(dbRec.cow_litres || dbRec.cowLitres || 0);
            let bufL = parseFloat(dbRec.buf_litres || dbRec.bufLitres || 0);
            const tL = parseFloat(dbRec.total_litres || 0);
            if (cowL === 0 && bufL === 0 && tL > 0) {
              if (masterBufSum > 0 && masterCowSum === 0) bufL = tL;
              else cowL = tL;
            }
            if (isAbsent) { cowL = 0; bufL = 0; }
            const dayTotal = Math.round((cowL + bufL) * 100) / 100;
            return {
              day: dayNum,
              dayOfWeek: dateObj.getDay(),
              cowLitres: cowL,
              bufLitres: bufL,
              litres: dayTotal,
              isAbsent: isCustOnDemand ? (dayTotal <= 0) : isAbsent
            };
          }
          const cDef = isCustOnDemand ? 0 : defaultCowQuota;
          const bDef = isCustOnDemand ? 0 : defaultBufQuota;
          const dayTotal = Math.round((cDef + bDef) * 100) / 100;
          return {
            day: dayNum,
            dayOfWeek: dateObj.getDay(),
            cowLitres: cDef,
            bufLitres: bDef,
            litres: dayTotal,
            isAbsent: isCustOnDemand ? true : false
          };
        });
        setCalendarDays(mapped);
      } else {
        const initial = Array.from({ length: daysInMonth }, (_, i) => {
          const dayNum = i + 1;
          const dateObj = new Date(yr, mo - 1, dayNum);
          const cDef = isCustOnDemand ? 0 : defaultCowQuota;
          const bDef = isCustOnDemand ? 0 : defaultBufQuota;
          const dayTotal = Math.round((cDef + bDef) * 100) / 100;
          return {
            day: dayNum,
            dayOfWeek: dateObj.getDay(),
            cowLitres: cDef,
            bufLitres: bDef,
            litres: dayTotal,
            isAbsent: isCustOnDemand ? true : false
          };
        });
        setCalendarDays(initial);
      }
    } catch (e) {
      const initial = Array.from({ length: daysInMonth }, (_, i) => {
        const dayNum = i + 1;
        const dateObj = new Date(yr, mo - 1, dayNum);
        const cDef = isCustOnDemand ? 0 : defaultCowQuota;
        const bDef = isCustOnDemand ? 0 : defaultBufQuota;
        const dayTotal = Math.round((cDef + bDef) * 100) / 100;
        return {
          day: dayNum,
          dayOfWeek: dateObj.getDay(),
          cowLitres: cDef,
          bufLitres: bDef,
          litres: dayTotal,
          isAbsent: isCustOnDemand ? true : false
        };
      });
      setCalendarDays(initial);
    } finally {
      setLoadingCalendar(false);
    }
  };

  // Quick Calendar Actions
  const handleApplyDefaultQuota = () => {
    setCalendarDays(prev => prev.map(d => ({
      ...d,
      cowLitres: calendarDefaultCowQuota,
      bufLitres: calendarDefaultBufQuota,
      litres: Math.round((calendarDefaultCowQuota + calendarDefaultBufQuota) * 100) / 100,
      isAbsent: (calendarDefaultCowQuota + calendarDefaultBufQuota) === 0
    })));
  };

  const handleMarkSundaysSkipped = () => {
    setCalendarDays(prev => prev.map(d => {
      if (d.dayOfWeek === 0) {
        return { ...d, cowLitres: 0, bufLitres: 0, litres: 0, isAbsent: true };
      }
      return d;
    }));
  };

  const handleMarkAllDelivered = () => {
    setCalendarDays(prev => prev.map(d => {
      const cQ = calendarDefaultCowQuota;
      const bQ = calendarDefaultBufQuota;
      const hasAny = (cQ + bQ) > 0;
      return {
        ...d,
        cowLitres: hasAny ? cQ : (calendarDefaultBufQuota > 0 ? 0 : 1),
        bufLitres: bQ,
        litres: hasAny ? (cQ + bQ) : 1,
        isAbsent: false
      };
    }));
  };

  const handleToggleDaySkip = (dayIndex) => {
    setCalendarDays(prev => prev.map((d, idx) => {
      if (idx !== dayIndex) return d;
      const willBeAbsent = !d.isAbsent;
      if (willBeAbsent) {
        return {
          ...d,
          isAbsent: true,
          cowLitres: 0,
          bufLitres: 0,
          litres: 0
        };
      } else {
        const cQ = (d.cowLitres > 0 || d.bufLitres > 0) ? d.cowLitres : calendarDefaultCowQuota;
        const bQ = (d.cowLitres > 0 || d.bufLitres > 0) ? d.bufLitres : calendarDefaultBufQuota;
        const tot = Math.round((cQ + bQ) * 100) / 100;
        return {
          ...d,
          isAbsent: false,
          cowLitres: tot > 0 ? cQ : (calendarDefaultBufQuota > 0 ? 0 : 1),
          bufLitres: tot > 0 ? bQ : (calendarDefaultBufQuota > 0 ? calendarDefaultBufQuota : 0),
          litres: tot > 0 ? tot : 1
        };
      }
    }));
  };

  const handleChangeDayLitres = (dayIndex, type, delta) => {
    setCalendarDays(prev => prev.map((d, idx) => {
      if (idx !== dayIndex) return d;
      let newCow = parseFloat(d.cowLitres || 0);
      let newBuf = parseFloat(d.bufLitres || 0);
      if (type === 'cow') {
        newCow = Math.max(0, parseFloat((newCow + delta).toFixed(1)));
      } else {
        newBuf = Math.max(0, parseFloat((newBuf + delta).toFixed(1)));
      }
      const dayTotal = Math.round((newCow + newBuf) * 100) / 100;
      return {
        ...d,
        cowLitres: newCow,
        bufLitres: newBuf,
        litres: dayTotal,
        isAbsent: dayTotal === 0
      };
    }));
  };

  const handleSetDayLitres = (dayIndex, type, value) => {
    const parsed = parseFloat(value);
    const safeVal = isNaN(parsed) ? 0 : Math.max(0, parsed);
    setCalendarDays(prev => prev.map((d, idx) => {
      if (idx !== dayIndex) return d;
      let newCow = parseFloat(d.cowLitres || 0);
      let newBuf = parseFloat(d.bufLitres || 0);
      if (type === 'cow') {
        newCow = safeVal;
      } else {
        newBuf = safeVal;
      }
      const dayTotal = Math.round((newCow + newBuf) * 100) / 100;
      return {
        ...d,
        cowLitres: newCow,
        bufLitres: newBuf,
        litres: dayTotal,
        isAbsent: dayTotal === 0
      };
    }));
  };

  // Save Calendar Changes and Recalculate Actual Live Bill
  const handleSaveCalendar = async () => {
    if (!calendarCustomer) return;
    setSavingCalendar(true);
    setCalendarToast(null);

    const payload = {
      year_month: selectedMonth,
      days: calendarDays.map(d => {
        const cL = d.isAbsent ? 0 : parseFloat(d.cowLitres || 0);
        const bL = d.isAbsent ? 0 : parseFloat(d.bufLitres || 0);
        return {
          day: d.day,
          cow_litres: cL,
          buf_litres: bL,
          litres: Math.round((cL + bL) * 100) / 100,
          isAbsent: Boolean(d.isAbsent) || (cL + bL === 0)
        };
      })
    };

    try {
      const res = await fetch(`${API_BASE}/tenant/${tenantSlug}/customers/${calendarCustomer.customerId}/attendance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      await res.json();

      // Immediately re-fetch live billing summary to update the main table, invoice, and WhatsApp texts!
      await fetchLiveBilling();

      setCalendarToast({
        type: 'success',
        text: isMr ? 'दूध वाटप (गाय/म्हैस) व खाडे नोंद यशस्वीरीत्या सेव्ह झाली! बिल तात्काळ अपडेट झाले आहे.' : 'Milk delivery (Cow/Buf) & skipped days saved! Bill updated successfully.'
      });

      setTimeout(() => {
        setCalendarCustomer(null);
        setCalendarToast(null);
      }, 750);
    } catch (err) {
      console.error('Save calendar error:', err);
      setCalendarToast({
        type: 'error',
        text: isMr ? 'नोंद सेव्ह करताना त्रुटी आली!' : 'Failed to save records!'
      });
    } finally {
      setSavingCalendar(false);
    }
  };

  // Modal Dynamic Computed Metrics
  const modalCowLitres = parseFloat(calendarDays.reduce((sum, d) => sum + (d.isAbsent ? 0 : (parseFloat(d.cowLitres) || 0)), 0).toFixed(1));
  const modalBufLitres = parseFloat(calendarDays.reduce((sum, d) => sum + (d.isAbsent ? 0 : (parseFloat(d.bufLitres) || 0)), 0).toFixed(1));
  const modalTotalLitres = parseFloat((modalCowLitres + modalBufLitres).toFixed(1));
  const modalDeliveredDays = calendarDays.filter(d => !d.isAbsent && ((parseFloat(d.cowLitres) || 0) + (parseFloat(d.bufLitres) || 0)) > 0).length;
  const modalSkippedDays = calendarDays.filter(d => d.isAbsent || ((parseFloat(d.cowLitres) || 0) + (parseFloat(d.bufLitres) || 0)) === 0).length;
  const modalEstMilkAmount = Math.round((modalCowLitres * calendarCowRate) + (modalBufLitres * calendarBufRate));

  // Generate WhatsApp Message with Digital Invoice URL
  const generateWhatsAppText = (rec) => {
    const dairyName = isMr 
      ? (effectiveTenant?.brandMarathi || effectiveTenant?.brand_name_mr || effectiveTenant?.name || 'दूध संकलन केंद्र')
      : (effectiveTenant?.brandEnglish || effectiveTenant?.brand_name_en || effectiveTenant?.name || 'Milk Collection Center');

    const [yr, mo] = selectedMonth.split('-');
    const monthNamesMr = { '01': 'जानेवारी', '02': 'फेब्रुवारी', '03': 'मार्च', '04': 'एप्रिल', '05': 'मे', '06': 'जून', '07': 'जुलै', '08': 'ऑगस्ट', '09': 'सप्टेंबर', '10': 'ऑक्टोबर', '11': 'नोव्हेंबर', '12': 'डिसेंबर' };
    const monthName = isMr ? (monthNamesMr[mo] || mo) + ' ' + yr : selectedMonth;

    const baseUrl = (typeof window !== 'undefined' && window.location?.origin && window.location.origin.startsWith('http'))
      ? window.location.origin
      : 'https://milkpromitra.esensevvg.online';
    const invoiceUrl = `${baseUrl}/#invoice-view?slug=${encodeURIComponent(tenantSlug)}&customerId=${encodeURIComponent(rec.customerId)}&month=${encodeURIComponent(selectedMonth)}`;

    const text = isMr ? `*🥛 ${dairyName} - मासिक दूध बिल पावती*
ग्राहक: *${rec.customerName}*
बिलिंग महिना: *${monthName}*
------------------------------
🥛 एकूण दूध: *${rec.totalMilkLiters} L* (${rec.deliveredDays} दिवस)
💵 दूध बिल रक्कम: *₹${rec.milkAmount}*
${isByproductsEnabled && rec.extraAmount > 0 ? `📦 उप-उत्पादने: *₹${rec.extraAmount}*\n` : ''}${rec.prevBalance > 0 ? `⏮️ मागील बाकी: *₹${rec.prevBalance}*\n` : ''}------------------------------
💰 *एकूण देय रक्कम: ₹${rec.totalBill}*
${rec.paymentStatus === 'PAID' ? '✅ *स्थिती: पूर्ण जमा झाले*' : `⚠️ *शिल्लक बाकी: ₹${rec.pendingAmount}*`}

📲 *आपली डिजिटल बिल पावती व हिशोब पाहण्यासाठी खालील लिंकवर क्लिक करा:*
${invoiceUrl}

_MilkPro Mitra डिजिटल बिलिंग प्रणाली_`
: `*🥛 ${dairyName} - Monthly Milk Invoice 🥛*
Customer: *${rec.customerName}*
Month: *${monthName}*
------------------------------
🥛 Total Milk: *${rec.totalMilkLiters} L* (${rec.deliveredDays} days)
💵 Milk Amount: *₹${rec.milkAmount}*
${isByproductsEnabled && rec.extraAmount > 0 ? `📦 Byproducts: *₹${rec.extraAmount}*\n` : ''}${rec.prevBalance > 0 ? `⏮️ Previous Balance: *₹${rec.prevBalance}*\n` : ''}------------------------------
💰 *Total Amount: ₹${rec.totalBill}*
${rec.paymentStatus === 'PAID' ? '✅ *Status: Paid*' : `⚠️ *Due Amount: ₹${rec.pendingAmount}*`}

📲 *Click to view your itemized digital invoice:*
${invoiceUrl}`;

    return encodeURIComponent(text);
  };

  const dayNamesMr = ['रवि', 'सोम', 'मंगळ', 'बुध', 'गुरू', 'शुक्र', 'शनि'];
  const dayNamesEn = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div style={{ padding: '1rem', maxWidth: 1200, margin: '0 auto' }}>
      {/* Top Banner with Center & Route Filter Selectors */}
      <div style={{
        background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
        borderRadius: 16,
        padding: '1.25rem',
        color: '#ffffff',
        marginBottom: '1.25rem',
        boxShadow: '0 10px 25px -5px rgba(2, 132, 199, 0.25)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <span style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', background: 'rgba(255,255,255,0.2)', padding: '3px 8px', borderRadius: 6 }}>
              {isMr ? 'मासिक बिलिंग व खातावही' : 'Monthly Ledger & Billing'}
            </span>
            <h1 style={{ fontSize: '1.4rem', fontWeight: 900, margin: '6px 0 2px 0' }}>
              {isMr ? 'मासिक बिलिंग व खातावही' : 'Customer Monthly Milk Billing & Invoices'}
            </h1>
            <p style={{ fontSize: '0.8rem', opacity: 0.9, margin: 0 }}>
              {isMr ? 'महिन्यावार दूध बिल, १-क्लिक व्हॉट्सॲप बिल पावती व वसुली हिशोब' : 'Monthly milk billing, 1-click WhatsApp bill receipts and collection ledger'}
            </p>
          </div>

          {/* Center, Route (Line), Month & Report Controls */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Center Selector Filter */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              background: 'rgba(255,255,255,0.18)',
              borderRadius: 8,
              padding: '0 0.5rem',
              border: '1px solid rgba(255,255,255,0.3)'
            }}>
              <Building2 size={14} style={{ color: '#ffffff', opacity: 0.9, marginRight: 4 }} />
              <select
                value={selectedCenterId}
                onChange={(e) => {
                  setSelectedCenterId(e.target.value);
                  setSelectedLineId('ALL');
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: '0.82rem',
                  padding: '0.45rem 0.2rem',
                  outline: 'none',
                  cursor: 'pointer'
                }}
                title={isMr ? 'सेंटर निवडा' : 'Select Center'}
              >
                <option value="ALL" style={{ color: '#0f172a' }}>{isMr ? 'सर्व सेंटर्स' : 'All Centers'}</option>
                {internalCenters.map(c => (
                  <option key={c.id} value={c.id} style={{ color: '#0f172a' }}>
                    {isMr ? (c.name_mr || c.name || `सेंटर #${c.id}`) : (c.name_en || c.name || `Center #${c.id}`)}
                  </option>
                ))}
              </select>
            </div>

            {/* Route / Line Selector Filter */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              background: 'rgba(255,255,255,0.18)',
              borderRadius: 8,
              padding: '0 0.5rem',
              border: '1px solid rgba(255,255,255,0.3)'
            }}>
              <Navigation size={14} style={{ color: '#ffffff', opacity: 0.9, marginRight: 4 }} />
              <select
                value={selectedLineId}
                onChange={(e) => setSelectedLineId(e.target.value)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: '0.82rem',
                  padding: '0.45rem 0.2rem',
                  outline: 'none',
                  cursor: 'pointer'
                }}
                title={isMr ? 'मार्ग (Route) निवडा' : 'Select Route/Line'}
              >
                <option value="ALL" style={{ color: '#0f172a' }}>{isMr ? 'सर्व मार्ग (Routes)' : 'All Routes'}</option>
                {availableLines.map(l => (
                  <option key={l.id} value={l.id} style={{ color: '#0f172a' }}>
                    {isMr ? (l.name_mr || l.name || `मार्ग #${l.id}`) : (l.name_en || l.name || `Route #${l.id}`)}
                  </option>
                ))}
              </select>
            </div>

            {/* Month Picker */}
            <input 
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              style={{
                padding: '0.45rem 0.75rem',
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.3)',
                background: 'rgba(255,255,255,0.18)',
                color: '#ffffff',
                fontWeight: 700,
                fontSize: '0.82rem',
                outline: 'none',
                cursor: 'pointer'
              }}
            />

            {/* Collect Payment Quick Button */}
            {onNavigateToPayments && (
              <button
                onClick={onNavigateToPayments}
                style={{
                  padding: '0.45rem 0.85rem',
                  borderRadius: 8,
                  border: 'none',
                  background: '#16a34a',
                  color: '#ffffff',
                  fontWeight: 800,
                  fontSize: '0.82rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  cursor: 'pointer',
                  boxShadow: '0 2px 6px rgba(22, 163, 74, 0.3)'
                }}
                title={isMr ? 'ग्राहक पेमेंट पावती बनवा' : 'Record Customer Payment'}
              >
                <CreditCard size={14} />
                <span>{isMr ? '+ पेमेंट जमा' : '+ Collect Payment'}</span>
              </button>
            )}

            {/* Auto-Scan Mismatched Records Button */}
            <button
              onClick={handleOpenAutoScan}
              style={{
                padding: '0.45rem 0.85rem',
                borderRadius: 8,
                border: 'none',
                background: '#f59e0b',
                color: '#ffffff',
                fontWeight: 800,
                fontSize: '0.82rem',
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                cursor: 'pointer',
                boxShadow: '0 2px 6px rgba(245, 158, 11, 0.35)'
              }}
              title={isMr ? 'विसंगत व अर्धवट नोंदी ऑटो-स्कॅन करा' : 'Auto-Scan Mismatches'}
            >
              <span>🔍 {isMr ? 'ऑटो-स्कॅन' : 'Auto-Scan'}</span>
            </button>

            {/* Print / Report Button */}
            <button
              onClick={() => window.print()}
              style={{
                padding: '0.45rem 0.85rem',
                borderRadius: 8,
                border: 'none',
                background: '#ffffff',
                color: '#0284c7',
                fontWeight: 800,
                fontSize: '0.82rem',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                cursor: 'pointer'
              }}
            >
              <Download size={14} />
              <span>{isMr ? 'प्रिंट' : 'Print'}</span>
            </button>
          </div>
        </div>

        {/* Financial KPI Summary based on filtered records */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
          gap: 10,
          marginTop: '1.25rem'
        }}>
          <div style={{ background: 'rgba(255,255,255,0.12)', padding: '0.75rem', borderRadius: 10 }}>
            <div style={{ fontSize: '0.7rem', opacity: 0.85 }}>{isMr ? 'एकूण ग्राहक' : 'Total Customers'}</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 900 }}>{filtered.length}</div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.12)', padding: '0.75rem', borderRadius: 10 }}>
            <div style={{ fontSize: '0.7rem', opacity: 0.85 }}>{isMr ? 'एकूण बिल आकारणी' : 'Total Billed'}</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 900 }}>₹{totalBilled.toLocaleString()}</div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.12)', padding: '0.75rem', borderRadius: 10 }}>
            <div style={{ fontSize: '0.7rem', opacity: 0.85 }}>{isMr ? 'एकूण जमा रक्कम' : 'Total Received'}</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 900, color: '#a7f3d0' }}>₹{totalReceived.toLocaleString()}</div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.12)', padding: '0.75rem', borderRadius: 10 }}>
            <div style={{ fontSize: '0.7rem', opacity: 0.85 }}>{isMr ? 'शिल्लक बाकी' : 'Total Due'}</div>
            <div style={{ fontSize: '1.25rem', fontWeight: 900, color: '#fecaca' }}>₹{totalDue.toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div style={{
        background: '#ffffff',
        borderRadius: 14,
        padding: '0.75rem 1rem',
        marginBottom: '1rem',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 10,
        justifyContent: 'space-between',
        alignItems: 'center',
        border: '1px solid #e2e8f0',
        boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
      }}>
        <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: 360 }}>
          <Search size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input
            type="text"
            placeholder={isMr ? 'ग्राहक शोधा (नाव, फोन, पत्ता)...' : 'Search customer...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '0.45rem 0.75rem 0.45rem 2.2rem',
              borderRadius: 8,
              border: '1px solid #cbd5e1',
              fontSize: '0.85rem',
              outline: 'none'
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {['ALL', 'PENDING', 'PAID'].map(st => (
            <button
              key={st}
              onClick={() => setFilterStatus(st)}
              style={{
                padding: '0.4rem 0.75rem',
                borderRadius: 8,
                border: filterStatus === st ? 'none' : '1px solid #e2e8f0',
                background: filterStatus === st ? '#0284c7' : '#f8fafc',
                color: filterStatus === st ? '#ffffff' : '#475569',
                fontWeight: 700,
                fontSize: '0.78rem',
                cursor: 'pointer'
              }}
            >
              {st === 'ALL' ? (isMr ? 'सर्व' : 'All') : st === 'PENDING' ? (isMr ? 'बाकीदार' : 'Due') : (isMr ? 'पूर्ण जमा' : 'Paid')}
            </button>
          ))}
        </div>
      </div>

      {/* Main Billing Table with Conditional 'इतर उत्पादने' Column */}
      <div style={{
        background: '#ffffff',
        borderRadius: 14,
        overflow: 'hidden',
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.03)'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', textAlign: 'left' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', color: '#475569', fontWeight: 800 }}>
                <th style={{ padding: '0.85rem 1rem' }}>#</th>
                <th style={{ padding: '0.85rem 1rem' }}>{isMr ? 'ग्राहक / पत्ता' : 'Customer / Address'}</th>
                <th style={{ padding: '0.85rem 1rem', textAlign: 'center' }}>{isMr ? 'एकूण लिटर' : 'Total Liters'}</th>
                <th style={{ padding: '0.85rem 1rem', textAlign: 'right' }}>{isMr ? 'दूध रक्कम' : 'Milk Amt'}</th>
                {isByproductsEnabled && (
                  <th style={{ padding: '0.85rem 1rem', textAlign: 'right' }}>{isMr ? 'इतर उत्पादने' : 'Other Items'}</th>
                )}
                <th style={{ padding: '0.85rem 1rem', textAlign: 'right' }}>{isMr ? 'मागील बाकी' : 'Prev Due'}</th>
                <th style={{ padding: '0.85rem 1rem', textAlign: 'right' }}>{isMr ? 'एकूण देय' : 'Total Bill'}</th>
                <th style={{ padding: '0.85rem 1rem', textAlign: 'center' }}>{isMr ? 'स्थिती' : 'Status'}</th>
                <th style={{ padding: '0.85rem 1rem', textAlign: 'center' }}>{isMr ? 'कृती' : 'Actions'}</th>
              </tr>
            </thead>
            <tbody>
              {loadingBilling ? (
                <tr>
                  <td colSpan={isByproductsEnabled ? 10 : 9} style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                    {isMr ? 'बिलिंग डेटा लोड होत आहे...' : 'Loading live billing records...'}
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={isByproductsEnabled ? 10 : 9} style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>
                    {isMr ? 'कोणतेही रेकॉर्ड सापडले नाहीत' : 'No billing records found'}
                  </td>
                </tr>
              ) : (
                filtered.map((rec, idx) => (
                  <tr key={rec.customerId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '0.85rem 1rem', color: '#94a3b8', fontWeight: 700 }}>{idx + 1}</td>
                    <td style={{ padding: '0.85rem 1rem' }}>
                      <div style={{ fontWeight: 800, color: '#0f172a' }}>{rec.customerName}</div>
                      <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                        {rec.address || rec.phone || (rec.lineName ? `${rec.lineName}` : '')}
                      </div>
                    </td>
                    <td style={{ padding: '0.85rem 1rem', textAlign: 'center' }}>
                      <div style={{ fontWeight: 800, color: '#0369a1' }}>{rec.totalMilkLiters} L</div>
                      <div style={{ fontSize: '0.7rem', color: '#64748b' }}>({rec.deliveredDays} {isMr ? 'दिवस' : 'days'})</div>
                    </td>
                    <td style={{ padding: '0.85rem 1rem', textAlign: 'right', fontWeight: 700 }}>
                      ₹{rec.milkAmount}
                    </td>

                    {/* Conditional Other Byproducts Column */}
                    {isByproductsEnabled && (
                      <td style={{ padding: '0.85rem 1rem', textAlign: 'right', color: rec.extraAmount > 0 ? '#059669' : '#94a3b8', fontWeight: 600 }}>
                        {rec.extraAmount > 0 ? `₹${rec.extraAmount}` : '-'}
                      </td>
                    )}

                    <td style={{ padding: '0.85rem 1rem', textAlign: 'right', color: rec.prevBalance > 0 ? '#b91c1c' : '#64748b' }}>
                      ₹{rec.prevBalance}
                    </td>
                    <td style={{ padding: '0.85rem 1rem', textAlign: 'right', fontWeight: 900, color: '#0284c7', fontSize: '0.92rem' }}>
                      ₹{rec.totalBill}
                    </td>
                    <td style={{ padding: '0.85rem 1rem', textAlign: 'center' }}>
                      <span style={{
                        fontSize: '0.7rem',
                        fontWeight: 800,
                        padding: '2px 8px',
                        borderRadius: 6,
                        background: rec.paymentStatus === 'PAID' ? '#dcfce7' : '#fee2e2',
                        color: rec.paymentStatus === 'PAID' ? '#15803d' : '#b91c1c'
                      }}>
                        {rec.paymentStatus === 'PAID' 
                          ? (isMr ? 'पूर्ण जमा' : 'Paid') 
                          : (isMr ? `बाकी ₹${rec.pendingAmount}` : `Due ₹${rec.pendingAmount}`)}
                      </span>
                    </td>
                    <td style={{ padding: '0.85rem 1rem', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'center', alignItems: 'center' }}>
                        {/* 1. Edit Calendar (दूध नोंद / खाडे) Button */}
                        <button
                          onClick={() => handleOpenCalendar(rec)}
                          title={isMr ? 'दूध वाटप व खाडे नोंद एडिट करा' : 'Edit Milk Delivery & Skipped Days (Khade)'}
                          style={{
                            padding: '0.35rem 0.6rem',
                            borderRadius: 6,
                            border: '1px solid #bfdbfe',
                            background: '#eff6ff',
                            cursor: 'pointer',
                            color: '#1d4ed8',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            fontWeight: 700,
                            fontSize: '0.72rem'
                          }}
                        >
                          <Calendar size={13} />
                          <span>{isMr ? 'खाडे/नोंद' : 'Calendar'}</span>
                        </button>

                        {/* 1.5 View Customer Ledger Button */}
                        <button
                          onClick={() => setSelectedLedgerCustomer(rec)}
                          title={isMr ? 'ग्राहक खातावही व लेजर पहा' : 'View Customer Ledger'}
                          style={{
                            padding: '0.35rem 0.6rem',
                            borderRadius: 6,
                            border: '1px solid #c7d2fe',
                            background: '#eef2ff',
                            cursor: 'pointer',
                            color: '#4338ca',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            fontWeight: 700,
                            fontSize: '0.72rem'
                          }}
                        >
                          <BookOpen size={13} />
                          <span>{isMr ? 'खातावही' : 'Ledger'}</span>
                        </button>

                        {/* 2. View Bill Invoice Button */}
                        <button
                          onClick={() => setModalInvoice(rec)}
                          title={isMr ? 'बिल पावती पहा' : 'View Bill Invoice'}
                          style={{
                            padding: '0.35rem 0.55rem',
                            borderRadius: 6,
                            border: '1px solid #cbd5e1',
                            background: '#f8fafc',
                            cursor: 'pointer',
                            color: '#0f172a'
                          }}
                        >
                          <Eye size={14} />
                        </button>

                        {/* 3. WhatsApp Share Button */}
                        {isWhatsappFeatureOn && (
                          <a
                            href={`https://wa.me/91${(rec.phone || '').replace(/\D/g, '')}?text=${generateWhatsAppText(rec)}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              padding: '0.35rem 0.65rem',
                              borderRadius: 6,
                              border: 'none',
                              background: '#16a34a',
                              color: '#ffffff',
                              fontWeight: 700,
                              fontSize: '0.72rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              textDecoration: 'none'
                            }}
                            title={isMr ? 'व्हॉट्सॲपवर डिजिटल बिल लिंक पाठवा' : 'Share digital invoice link on WhatsApp'}
                          >
                            <Share2 size={13} />
                            <span>WhatsApp</span>
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Monthly Milk Delivery & Skipped Days (खाडे) Modal */}
      {calendarCustomer && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.7)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '0.75rem'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: 18,
            maxWidth: 740,
            width: '100%',
            maxHeight: '92vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.35)'
          }}>
            {/* Modal Header */}
            <div style={{
              background: '#0f172a',
              color: '#ffffff',
              padding: '0.85rem 1.25rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <div style={{ fontSize: '0.7rem', color: '#38bdf8', fontWeight: 800, textTransform: 'uppercase' }}>
                  {isMr ? '॥ मासिक दूध नोंद व खाडे संपादन ॥' : '॥ MONTHLY MILK DELIVERIES & SKIPPED DAYS ॥'}
                </div>
                <div style={{ fontSize: '1.05rem', fontWeight: 900 }}>
                  {calendarCustomer.customerName}
                </div>
                <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                  {isMr ? 'मार्ग:' : 'Route:'} {calendarCustomer.lineName || 'Line #1'} • {isMr ? 'महिना:' : 'Month:'} {selectedMonth}
                </div>
              </div>
              <button
                onClick={() => setCalendarCustomer(null)}
                style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '1rem', overflowY: 'auto', flex: 1 }}>
              {/* Dynamic Live KPI Summary Bar: Cow, Buffalo, Total & Bill */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                gap: 8,
                marginBottom: '1rem'
              }}>
                <div style={{ background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 10, padding: '0.55rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.68rem', color: '#15803d', fontWeight: 800 }}>🐄 {isMr ? 'गाय दूध' : 'Cow Milk'}</div>
                  <div style={{ fontSize: '1.10rem', fontWeight: 900, color: '#16a34a' }}>{modalCowLitres} L</div>
                  <div style={{ fontSize: '0.65rem', color: '#65a30d', fontWeight: 700 }}>@ ₹{calendarCowRate}/L</div>
                </div>

                <div style={{ background: '#f0f9ff', border: '1.5px solid #7dd3fc', borderRadius: 10, padding: '0.55rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.68rem', color: '#0369a1', fontWeight: 800 }}>🐃 {isMr ? 'म्हैस दूध' : 'Buffalo Milk'}</div>
                  <div style={{ fontSize: '1.10rem', fontWeight: 900, color: '#0284c7' }}>{modalBufLitres} L</div>
                  <div style={{ fontSize: '0.65rem', color: '#0284c7', fontWeight: 700 }}>@ ₹{calendarBufRate}/L</div>
                </div>

                <div style={{ background: '#ffffff', border: '1.5px solid #cbd5e1', borderRadius: 10, padding: '0.55rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.68rem', color: '#475569', fontWeight: 800 }}>🥛 {isMr ? 'एकूण दूध' : 'Total Litres'}</div>
                  <div style={{ fontSize: '1.10rem', fontWeight: 900, color: '#0f172a' }}>{modalTotalLitres} L</div>
                  <div style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 700 }}>{modalDeliveredDays} {isMr ? 'हजर' : 'days'} • {modalSkippedDays} {isMr ? 'खाडे' : 'off'}</div>
                </div>

                <div style={{ background: '#fef08a', border: '1.5px solid #facc15', borderRadius: 10, padding: '0.55rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.68rem', color: '#854d0e', fontWeight: 800 }}>💰 {isMr ? 'अंदाजे बिल' : 'Est. Milk Bill'}</div>
                  <div style={{ fontSize: '1.15rem', fontWeight: 900, color: '#854d0e' }}>₹{modalEstMilkAmount}</div>
                  <div style={{ fontSize: '0.65rem', color: '#a16207', fontWeight: 700 }}>{isMr ? 'अचूक दरानुसार' : 'Rate calculated'}</div>
                </div>
              </div>

              {/* Quick Preset Action Buttons */}
              <div style={{
                background: '#f8fafc',
                border: '1px dashed #cbd5e1',
                borderRadius: 10,
                padding: '0.6rem 0.85rem',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 6
              }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569' }}>
                  {isMr ? '⚡ झटपट कृती (Quick Actions):' : '⚡ Quick Actions:'}
                </span>

                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button
                    onClick={handleApplyDefaultQuota}
                    style={{
                      padding: '0.3rem 0.65rem',
                      borderRadius: 6,
                      border: '1px solid #cbd5e1',
                      background: '#ffffff',
                      color: '#0f172a',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    📋 {calendarDefaultQuota === 0 ? (isMr ? 'सर्व दिवस 0L करा' : 'Reset All to 0L') : (isMr ? `सर्व दिवस कोटा लागू (${calendarDefaultQuota}L)` : `Apply Quota (${calendarDefaultQuota}L)`)}
                  </button>

                  <button
                    onClick={handleMarkSundaysSkipped}
                    style={{
                      padding: '0.3rem 0.65rem',
                      borderRadius: 6,
                      border: '1px solid #fde68a',
                      background: '#fffbeb',
                      color: '#b45309',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    🏖️ {isMr ? 'सर्व रविवार खाडे करा' : 'Mark Sundays Skipped'}
                  </button>

                  <button
                    onClick={handleMarkAllDelivered}
                    style={{
                      padding: '0.3rem 0.65rem',
                      borderRadius: 6,
                      border: '1px solid #bbf7d0',
                      background: '#f0fdf4',
                      color: '#15803d',
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    🟢 {isMr ? 'सर्व दिवस दूध दिले' : 'Mark All Delivered'}
                  </button>
                </div>
              </div>

              {/* Toast Message inside modal */}
              {calendarToast && (
                <div style={{
                  padding: '0.6rem 0.85rem',
                  borderRadius: 8,
                  marginBottom: '0.85rem',
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  background: calendarToast.type === 'success' ? '#dcfce7' : '#fee2e2',
                  color: calendarToast.type === 'success' ? '#15803d' : '#b91c1c',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6
                }}>
                  {calendarToast.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
                  <span>{calendarToast.text}</span>
                </div>
              )}

              {/* 30/31 Days Interactive Grid with Separate Cow & Buffalo Controls */}
              {loadingCalendar ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                  {isMr ? 'हजेरी व वाटप नोंद लोड होत आहे...' : 'Loading delivery matrix...'}
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                  gap: 8
                }}>
                  {calendarDays.map((d, index) => {
                    const isSunday = d.dayOfWeek === 0;
                    const dayLabel = isMr ? (dayNamesMr[d.dayOfWeek] || '') : (dayNamesEn[d.dayOfWeek] || '');
                    const dayTotal = Math.round(((parseFloat(d.cowLitres) || 0) + (parseFloat(d.bufLitres) || 0)) * 100) / 100;
                    return (
                      <div
                        key={d.day}
                        style={{
                          borderRadius: 10,
                          padding: '0.50rem 0.55rem',
                          border: d.isAbsent 
                            ? '1.5px dashed #f59e0b' 
                            : '1.5px solid #e2e8f0',
                          background: d.isAbsent ? '#fffbeb' : '#ffffff',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 5,
                          boxShadow: d.isAbsent ? 'none' : '0 1px 3px rgba(0,0,0,0.03)'
                        }}
                      >
                        {/* Day Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{
                            fontWeight: 900,
                            fontSize: '0.82rem',
                            color: isSunday ? '#e11d48' : '#0f172a'
                          }}>
                            {d.day} {dayLabel}
                          </span>

                          <button
                            onClick={() => handleToggleDaySkip(index)}
                            style={{
                              border: 'none',
                              padding: '2px 6px',
                              borderRadius: 4,
                              fontSize: '0.65rem',
                              fontWeight: 800,
                              cursor: 'pointer',
                              background: d.isAbsent ? '#fee2e2' : '#dcfce7',
                              color: d.isAbsent ? '#b91c1c' : '#15803d'
                            }}
                          >
                            {d.isAbsent ? (isMr ? '⏸️ खाडा' : 'Skipped') : (isMr ? '🟢 हजर' : 'Delivered')}
                          </button>
                        </div>

                        {/* Row 1: Cow Milk Controls (गाय) */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 3,
                          opacity: d.isAbsent ? 0.35 : 1
                        }}>
                          <span style={{ fontSize: '0.70rem', fontWeight: 800, color: '#16a34a', whiteSpace: 'nowrap' }}>
                            🐄 {isMr ? 'गाय' : 'Cow'}:
                          </span>

                          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <button
                              disabled={d.isAbsent}
                              onClick={() => handleChangeDayLitres(index, 'cow', -0.5)}
                              style={{
                                width: 22,
                                height: 22,
                                borderRadius: 4,
                                border: '1px solid #cbd5e1',
                                background: '#f8fafc',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: d.isAbsent ? 'not-allowed' : 'pointer',
                                fontWeight: 800
                              }}
                            >
                              <Minus size={11} />
                            </button>

                            <input
                              type="number"
                              step="0.5"
                              min="0"
                              disabled={d.isAbsent}
                              value={d.cowLitres !== undefined ? d.cowLitres : 0}
                              onChange={(e) => handleSetDayLitres(index, 'cow', e.target.value)}
                              style={{
                                width: 38,
                                textAlign: 'center',
                                padding: '2px 0',
                                borderRadius: 4,
                                border: '1px solid #cbd5e1',
                                fontWeight: 800,
                                fontSize: '0.80rem',
                                color: '#0f172a',
                                background: d.isAbsent ? '#f8fafc' : '#ffffff'
                              }}
                            />

                            <button
                              disabled={d.isAbsent}
                              onClick={() => handleChangeDayLitres(index, 'cow', 0.5)}
                              style={{
                                width: 22,
                                height: 22,
                                borderRadius: 4,
                                border: '1px solid #cbd5e1',
                                background: '#f8fafc',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: d.isAbsent ? 'not-allowed' : 'pointer',
                                fontWeight: 800
                              }}
                            >
                              <Plus size={11} />
                            </button>
                          </div>
                        </div>

                        {/* Row 2: Buffalo Milk Controls (म्हैस) */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 3,
                          opacity: d.isAbsent ? 0.35 : 1
                        }}>
                          <span style={{ fontSize: '0.70rem', fontWeight: 800, color: '#0284c7', whiteSpace: 'nowrap' }}>
                            🐃 {isMr ? 'म्हैस' : 'Buf'}:
                          </span>

                          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <button
                              disabled={d.isAbsent}
                              onClick={() => handleChangeDayLitres(index, 'buf', -0.5)}
                              style={{
                                width: 22,
                                height: 22,
                                borderRadius: 4,
                                border: '1px solid #cbd5e1',
                                background: '#f8fafc',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: d.isAbsent ? 'not-allowed' : 'pointer',
                                fontWeight: 800
                              }}
                            >
                              <Minus size={11} />
                            </button>

                            <input
                              type="number"
                              step="0.5"
                              min="0"
                              disabled={d.isAbsent}
                              value={d.bufLitres !== undefined ? d.bufLitres : 0}
                              onChange={(e) => handleSetDayLitres(index, 'buf', e.target.value)}
                              style={{
                                width: 38,
                                textAlign: 'center',
                                padding: '2px 0',
                                borderRadius: 4,
                                border: '1px solid #cbd5e1',
                                fontWeight: 800,
                                fontSize: '0.80rem',
                                color: '#0f172a',
                                background: d.isAbsent ? '#f8fafc' : '#ffffff'
                              }}
                            />

                            <button
                              disabled={d.isAbsent}
                              onClick={() => handleChangeDayLitres(index, 'buf', 0.5)}
                              style={{
                                width: 22,
                                height: 22,
                                borderRadius: 4,
                                border: '1px solid #cbd5e1',
                                background: '#f8fafc',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: d.isAbsent ? 'not-allowed' : 'pointer',
                                fontWeight: 800
                              }}
                            >
                              <Plus size={11} />
                            </button>
                          </div>
                        </div>

                        {/* Day Total Footnote */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          borderTop: '1px dashed #e2e8f0',
                          paddingTop: 3,
                          marginTop: 1
                        }}>
                          <span style={{ fontSize: '0.66rem', color: '#64748b', fontWeight: 700 }}>
                            {isMr ? 'एकूण:' : 'Total:'}
                          </span>
                          <span style={{ fontSize: '0.76rem', fontWeight: 900, color: d.isAbsent ? '#94a3b8' : '#0f172a' }}>
                            {dayTotal} L
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{
              background: '#f8fafc',
              borderTop: '1px solid #e2e8f0',
              padding: '0.75rem 1.25rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <button
                onClick={() => setCalendarCustomer(null)}
                style={{
                  padding: '0.45rem 1rem',
                  borderRadius: 8,
                  border: '1px solid #cbd5e1',
                  background: '#ffffff',
                  color: '#475569',
                  fontWeight: 700,
                  fontSize: '0.82rem',
                  cursor: 'pointer'
                }}
              >
                {isMr ? 'रद्द करा' : 'Cancel'}
              </button>

              <button
                disabled={savingCalendar}
                onClick={handleSaveCalendar}
                style={{
                  padding: '0.5rem 1.25rem',
                  borderRadius: 8,
                  border: 'none',
                  background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                  color: '#ffffff',
                  fontWeight: 800,
                  fontSize: '0.85rem',
                  cursor: savingCalendar ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  boxShadow: '0 4px 12px rgba(2, 132, 199, 0.25)'
                }}
              >
                <Check size={16} />
                <span>
                  {savingCalendar 
                    ? (isMr ? 'सेव्ह व बिल अपडेट होत आहे...' : 'Saving & Recalculating...') 
                    : (isMr ? '💾 बदल सेव्ह करा व बिल अपडेट करा' : 'Save Changes & Update Bill')}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Ledger Modal */}
      {selectedLedgerCustomer && (
        <CustomerLedgerModal
          customer={selectedLedgerCustomer}
          tenantSlug={tenantSlug}
          currentTenant={effectiveTenant}
          lang={lang}
          initialMonth={selectedMonth}
          onClose={() => setSelectedLedgerCustomer(null)}
        />
      )}

      {/* Invoice Modal */}
      {modalInvoice && (
        <InvoiceModal 
          invoiceData={modalInvoice}
          currentTenant={effectiveTenant}
          onClose={() => setModalInvoice(null)}
          lang={lang}
          selectedMonth={selectedMonth}
        />
      )}
    
      {/* Auto-Scan & Audit Mismatches Modal */}
      {isAutoScanOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.7)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
          padding: '1rem'
        }}>
          <div style={{
            background: '#ffffff',
            borderRadius: 18,
            maxWidth: 680,
            width: '100%',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.35)'
          }}>
            {/* Modal Header */}
            <div style={{
              background: '#0f172a',
              color: '#ffffff',
              padding: '12px 18px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div>
                <div style={{ fontSize: '0.72rem', color: '#f59e0b', fontWeight: 800, textTransform: 'uppercase' }}>
                  ॥ डेटा ऑडिट व विसंगती तपासणी ॥
                </div>
                <div style={{ fontSize: '1.05rem', fontWeight: 900 }}>
                  🔍 अर्धवट व विसंगत नोंदी ऑटो-स्कॅन (महिना: {selectedMonth})
                </div>
              </div>
              <button
                onClick={() => setIsAutoScanOpen(false)}
                style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '1.2rem' }}
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '16px', overflowY: 'auto', flex: 1 }}>
              {scanLoading ? (
                <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#64748b' }}>
                  <div style={{ fontSize: '1.8rem', marginBottom: 8 }}>⏳</div>
                  <div style={{ fontWeight: 800 }}>डेटाबेसमधील रोजनिशी व मासिक हजेरी तपासत आहे...</div>
                </div>
              ) : scanResults.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem 1rem', background: '#f0fdf4', borderRadius: 12, border: '1.5px solid #bbf7d0' }}>
                  <div style={{ fontSize: '2rem', marginBottom: 6 }}>✅</div>
                  <div style={{ fontSize: '1.05rem', fontWeight: 900, color: '#15803d' }}>
                    सर्व डेटा १००% अचूक आहे!
                  </div>
                  <p style={{ fontSize: '0.82rem', color: '#475569', margin: '4px 0 0 0' }}>
                    या महिन्यातील रोजनिशी आणि मासिक खाडे संपादनमध्ये कोणतीही विसंगत किंवा अनाथ नोंद आढळली नाही.
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fffbeb', padding: '8px 12px', borderRadius: 8, border: '1px solid #fde68a' }}>
                    <span style={{ fontSize: '0.82rem', fontWeight: 800, color: '#92400e' }}>
                      ⚠️ एकूण {scanResults.length} संशयास्पद/विसंगत नोंदी आढळल्या:
                    </span>
                    <label style={{ fontSize: '0.78rem', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={selectedScanItems.length === scanResults.length}
                        onChange={e => {
                          if (e.target.checked) setSelectedScanItems(scanResults.map(m => m.id));
                          else setSelectedScanItems([]);
                        }}
                      />
                      <span>सर्व निवडा ({selectedScanItems.length}/{scanResults.length})</span>
                    </label>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '45vh', overflowY: 'auto' }}>
                    {scanResults.map(item => (
                      <div
                        key={item.id}
                        style={{
                          background: '#f8fafc',
                          border: '1px solid #e2e8f0',
                          borderRadius: 10,
                          padding: '10px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedScanItems.includes(item.id)}
                          onChange={e => {
                            if (e.target.checked) setSelectedScanItems(prev => [...prev, item.id]);
                            else setSelectedScanItems(prev => prev.filter(x => x !== item.id));
                          }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: 800, fontSize: '0.88rem', color: '#0f172a' }}>
                              {item.customer_name}
                            </span>
                            <span style={{ fontSize: '0.74rem', fontWeight: 800, background: '#fee2e2', color: '#dc2626', padding: '2px 8px', borderRadius: 6 }}>
                              दिनांक: {item.date}
                            </span>
                          </div>
                          <div style={{ fontSize: '0.78rem', color: '#b45309', marginTop: 3 }}>
                            {item.description}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div style={{ background: '#f8fafc', padding: '12px 16px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                type="button"
                onClick={() => setIsAutoScanOpen(false)}
                style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', fontWeight: 800, color: '#475569', cursor: 'pointer' }}
              >
                बंद करा
              </button>
              {scanResults.length > 0 && (
                <button
                  type="button"
                  onClick={handleResolveScan}
                  disabled={isResolvingScan || selectedScanItems.length === 0}
                  style={{
                    padding: '7px 16px',
                    borderRadius: 8,
                    border: 'none',
                    background: '#dc2626',
                    color: '#fff',
                    fontWeight: 900,
                    cursor: 'pointer',
                    boxShadow: '0 2px 6px rgba(220, 38, 38, 0.3)'
                  }}
                >
                  {isResolvingScan ? 'दुरुस्त करत आहे...' : '🗑️ निवडलेल्या (' + selectedScanItems.length + ') नोंदी हटवा / दुरुस्त करा'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

</div>
  );
}
